import cx from 'classnames'
import React, { useEffect, useState } from 'react'
import { animated, useTransition } from 'react-spring'

import { Outline } from '@cinch/oxd-components/button/src'
import { Arrow as Icon } from '@cinch/oxd-components/icons/src'
import SVG from '@cinch/oxd-components/svg/src'
import Text from '@cinch/oxd-components/text/src'
import Wrapper from '@cinch/oxd-components/wrapper/src'
import { isMobile } from '@cinch/oxd-utils/browser-support/src'

import { CompareVehicle } from '@cinch/oxd-redux/compare/src/interfaces'

import Item from './components/item/compare-bar-item.component'

import * as styles from './compare-bar.module.css'

export interface CompareBarProps {
  items: CompareVehicle[]
  showDrawer: boolean
  onRemove: (id: number) => void
  onOpen: () => void
  onClose: () => void
  goCompare: (ids: number[]) => void
  defaultExpanded?: boolean
}

const Row: React.FC<any> = ({ children, ...props }) => (
  <div className={styles.row} {...props}>
    <Wrapper>{children}</Wrapper>
  </div>
)

const transforms = {
  unique: true,
  initial: { transform: 'translate3d(0,100%,0)' },
  from: { transform: 'translate3d(0,100%,0)' },
  enter: { transform: 'translate3d(0,0,0)' },
  leave: { transform: 'translate3d(0,100%,0)' },
}

const sizes = { bar: 55, item: 85 }

const CompareBar: React.FC<CompareBarProps> = ({
  items = [],
  defaultExpanded = true,
  onOpen,
  onClose,
  onRemove,
  showDrawer,
  goCompare,
}) => {
  const [isExpanded, setExpanded] = useState<boolean>(defaultExpanded!)
  const mainTransitions = useTransition(showDrawer ? ['id'] : [], (item) => item, transforms)
  const expandTransitions = useTransition(isExpanded ? ['id'] : [], (item) => item, transforms)
  const itemTransitions = useTransition(isExpanded ? items : [], (item) => item.id, {
    ...transforms,
    initial: { ...transforms.initial, opacity: 0 },
    from: { ...transforms.from, opacity: 0 },
    enter: { ...transforms.enter, opacity: 1 },
    leave: { ...transforms.leave, opacity: 0 },
  })

  const handleClick = () => {
    setExpanded(!isExpanded)
    isExpanded ? onClose() : onOpen()
  }

  useEffect(() => {
    if (showDrawer) {
      const drawerHeight = isExpanded ? sizes.item * (isMobile() ? items.length : 1) : 0
      document.body.style.paddingBottom = `${sizes.bar + drawerHeight}px`
    }

    return () => {
      document.body.style.paddingBottom = null as any
    }
  }, [showDrawer, isExpanded])

  return mainTransitions.map(({ props: mainProps, key: mainKey }) => (
    <animated.div className={styles.bar} style={mainProps} key={mainKey} data-testid="compare-bar">
      <Row style={{ zIndex: 1000 }}>
        <div className={cx(styles.actions, { [styles.active]: isExpanded })}>
          <button className={styles.expandButton} onClick={handleClick} data-testid="compare-bar-drawer-btn">
            <Text>Compare {items.length} of 2 cars</Text>
            <SVG
              size={25}
              children={Icon}
              hasGradient={true}
              className={cx(styles.icon, { [styles.active]: isExpanded })}
            />
          </button>
          <Outline
            className={styles.cta}
            type="button"
            disabled={items.length !== 2}
            size={'mini'}
            onClick={() => goCompare(items.map((i) => i.id))}
            data-testid="compare-bar-cta"
          >
            Compare
          </Outline>
        </div>
      </Row>
      {expandTransitions.map(({ props: expandProps, key: expandKey }) => (
        <animated.div style={expandProps} key={expandKey} data-testid="compare-bar-drawer">
          <Row>
            <div className={styles.expanded}>
              {itemTransitions.map(({ item, props, key }) => (
                <animated.div key={key} style={props} className={styles.item} data-testid={`compare-bar-item-${key}`}>
                  <Item onClose={() => onRemove(item.id)} {...item} />
                </animated.div>
              ))}
            </div>
          </Row>
        </animated.div>
      ))}
    </animated.div>
  )) as any
}

export default CompareBar
